import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  TableConfigDto,
  TableRowDto,
} from '@asset-metrix/portal-gateway-client-js';
import { AsyncNotification } from '@asset-metrix/portal-ui';
import { useDisclosure } from '@mantine/hooks';

import { useUpdateTableRow } from '../../hooks/useUpdateTableRow';
import { invalidateTable } from '../../utils/queryCacheHelper';

import {
  isInvestmentAssumptionForm,
  transformRowData,
} from './investmentAssumptionModalHelpers';
import { UpdateTableRowModalProps } from './UpdateTableRowModal';

export type RowData = {
  rowId: string;
  data: TableRowDto['data'];
};

type State = {
  rows: RowData[];
};

export type UseUpdateTableRowModalPropertiesReturnValue = {
  updateTableRowModalProps?: UpdateTableRowModalProps;
  open: (data: RowData[]) => void;
};

export function useUpdateTableRowModalProperties(
  pageId: string,
  contentId: string,
  config: TableConfigDto,
  instanceId: string
): UseUpdateTableRowModalPropertiesReturnValue {
  const [isOpen, { open, close }] = useDisclosure();
  const [state, setState] = useState<State | null>(null);
  const [errorRowCount, setErrorRowCount] = useState(0);
  const [successRowCount, setSuccessRowCount] = useState(0);
  const { t } = useTranslation();

  const mutationSuccessHandler = () => {
    setSuccessRowCount((prevState) => {
      return prevState + 1;
    });
  };

  /**
   * Update request error count to show error message
   * E.g: Error updating 4 items
   */
  const mutationErrorHandler = () => {
    setErrorRowCount((prevState) => {
      return prevState + 1;
    });
  };

  const {
    updateRow,
    status: updateRowStatus,
    error,
    reset: resetUpdateRow,
  } = useUpdateTableRow({
    mutationSuccessHandler: mutationSuccessHandler,
    mutationErrorHandler: mutationErrorHandler,
  });

  /**
   * Show notification and close the popup when all requests are successful
   */
  useEffect(() => {
    if (successRowCount === state?.rows.length) {
      // update table data when all requests are successful
      // invalidate all table caches
      invalidateTable(config.cacheKey);
      setState(null);
      resetUpdateRow();
      setSuccessRowCount(0);
      close();
      setErrorRowCount(0);
      AsyncNotification.show(
        { message: t('app.table.workflowActionModal.successMessage') },
        'success'
      );
    }
  }, [
    close,
    config.cacheKey,
    contentId,
    pageId,
    resetUpdateRow,
    state?.rows.length,
    successRowCount,
    t,
  ]);

  const updateTableRowModalProps = useMemo<
    UpdateTableRowModalProps | undefined
  >(() => {
    if (state === null || !config.updateJsonSchema) {
      return undefined;
    }

    const resetStateAndClose = () => {
      setState(null);
      resetUpdateRow();
      setSuccessRowCount(0);
      close();
      setErrorRowCount(0);
    };

    return {
      contentId,
      instanceId,
      data: state.rows,
      errorMessage: error?.message,
      errorRowCount,
      successRowCount,
      setSuccessRowCount,
      jsonSchema: config.updateJsonSchema,
      schemaType: config.createRowConfig?.type ?? 'row',
      onApply: (data, context) => {
        // Update request for each row
        for (const item of state.rows) {
          const { rowId } = item;
          updateRow(contentId, rowId, { data, context });
        }
      },
      onClose: resetStateAndClose,
      opened: isOpen,
      updateRowStatus,
    };
  }, [
    state,
    config.createRowConfig,
    config.updateJsonSchema,
    contentId,
    error?.message,
    errorRowCount,
    successRowCount,
    isOpen,
    updateRowStatus,
    resetUpdateRow,
    close,
    updateRow,
    instanceId,
  ]);

  const openCallback = useCallback(
    (
      rowData: {
        rowId: string;
        file_name?: string;
        data: TableRowDto['data'];
      }[]
    ) => {
      setState({
        rows: isInvestmentAssumptionForm(config.createRowConfig?.type)
          ? transformRowData(rowData)
          : rowData,
      });
      open();
    },
    [open, config.createRowConfig]
  );

  return {
    open: openCallback,
    updateTableRowModalProps,
  };
}
